import { cn } from "@repo/utils";
import { Check, X, ShieldAlert, RotateCcw } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import type { PermissionAccess } from "@/lib/permissions";

interface PermissionCellProps {
  access: PermissionAccess;
  defaultAccess: PermissionAccess;
  isOverridden: boolean;
  isReadOnly?: boolean;
  /** When true, only show allowed/denied (no approval option). */
  viewOnly?: boolean;
  /** Called with the selected access level. */
  onChange: (access: PermissionAccess) => void;
}

const ACCESS_CONFIG: Record<
  PermissionAccess,
  {
    icon: typeof Check;
    label: string;
    shortLabel: string;
    activeClasses: string;
    inactiveClasses: string;
  }
> = {
  allowed: {
    icon: Check,
    label: "Allowed",
    shortLabel: "Allow",
    activeClasses:
      "bg-emerald-50 text-emerald-600 border-emerald-300 ring-1 ring-emerald-200",
    inactiveClasses:
      "bg-transparent text-muted-foreground/60 border-transparent hover:bg-emerald-50/50 hover:text-emerald-600",
  },
  denied: {
    icon: X,
    label: "Denied",
    shortLabel: "Deny",
    activeClasses: "bg-red-50 text-red-500 border-red-300 ring-1 ring-red-200",
    inactiveClasses:
      "bg-transparent text-muted-foreground/60 border-transparent hover:bg-red-50/50 hover:text-red-500",
  },
  requires_approval: {
    icon: ShieldAlert,
    label: "Requires Approval",
    shortLabel: "Approval",
    activeClasses:
      "bg-amber-50 text-amber-600 border-amber-300 ring-1 ring-amber-200",
    inactiveClasses:
      "bg-transparent text-muted-foreground/60 border-transparent hover:bg-amber-50/50 hover:text-amber-600",
  },
};

const ALL_OPTIONS: PermissionAccess[] = [
  "allowed",
  "denied",
  "requires_approval",
];
const VIEW_OPTIONS: PermissionAccess[] = ["allowed", "denied"];

export function PermissionCell({
  access,
  defaultAccess,
  isOverridden,
  isReadOnly = false,
  viewOnly = false,
  onChange,
}: PermissionCellProps) {
  const options = viewOnly ? VIEW_OPTIONS : ALL_OPTIONS;

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="relative inline-flex"
        role="radiogroup"
        aria-label="Permission access level"
      >
        <div
          className={cn(
            "inline-flex items-center rounded-lg border border-border/60 bg-muted/30 p-0.5 gap-0.5",
            isReadOnly && "opacity-70"
          )}
        >
          {options.map((option) => {
            const config = ACCESS_CONFIG[option];
            const Icon = config.icon;
            const isActive = access === option;

            return (
              <Tooltip key={option}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    aria-label={config.label}
                    disabled={isReadOnly}
                    onClick={() => onChange(option)}
                    className={cn(
                      "flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                      isActive ? config.activeClasses : config.inactiveClasses,
                      !isReadOnly && "cursor-pointer",
                      isReadOnly && "cursor-default"
                    )}
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    <span className="hidden sm:inline">
                      {config.shortLabel}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {config.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        {isOverridden && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-primary border border-white" />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="flex items-center gap-1">
                <RotateCcw className="h-3 w-3" />
                Default: {ACCESS_CONFIG[defaultAccess].label}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
